import type { Post } from '../types';

/**
 * Before/after image resolution, shared by the feed card and the printed
 * readiness record.
 *
 * There are two pairing mechanisms and they are mutually exclusive. A
 * structure-linked pair points at an earlier post and snapshots its images
 * (`beforePostId` + `beforePostImageUrl` / `beforePostThumbnailUrl`), so the
 * card renders with no extra reads. A self-contained pair carries both images
 * on the one doc (`beforeImageUrl` / `beforeThumbnailUrl`). Either snapshot
 * thumbnail can be permanently empty when the pair predates the Resize Images
 * extension, so every lookup falls through all four fields.
 */
export interface ResolvedPair {
  post: Post;
  beforeUri: string;
  afterUri: string;
  beforePlaceholder?: string;
  afterPlaceholder?: string;
}

/** `full` picks originals over thumbnails, for detail and print, where an
 *  upscaled 400px thumbnail reads as blurry. */
export function resolveBeforeImage(post: Post, full = false): string | undefined {
  return full
    ? post.beforePostImageUrl ||
        post.beforeImageUrl ||
        post.beforePostThumbnailUrl ||
        post.beforeThumbnailUrl
    : post.beforePostThumbnailUrl ||
        post.beforePostImageUrl ||
        post.beforeThumbnailUrl ||
        post.beforeImageUrl;
}

export function resolveAfterImage(post: Post, full = false): string | undefined {
  return full ? post.imageUrl || post.thumbnailUrl : post.thumbnailUrl || post.imageUrl;
}

/** True when this post carries a before image by either mechanism. */
export function isPairPost(post: Post): boolean {
  return Boolean(post.beforePostId || post.beforeImageUrl);
}

/** The renderable pair, or null when this post is not one (or is missing an
 *  image on either side, which happens while an upload is still in flight). */
export function resolvePair(post: Post, full = true): ResolvedPair | null {
  if (!isPairPost(post)) return null;
  const beforeUri = resolveBeforeImage(post, full);
  const afterUri = resolveAfterImage(post, full);
  if (!beforeUri || !afterUri) return null;
  return {
    post,
    beforeUri,
    afterUri,
    beforePlaceholder: full ? post.beforePostThumbnailUrl || post.beforeThumbnailUrl : undefined,
    afterPlaceholder: full ? post.thumbnailUrl : undefined,
  };
}
